import { useTranslate } from '@tolgee/react';
import { Button, TableColumnsType, TableProps, Tooltip } from 'antd';
import ScrollTable from 'components/ScrollTable';
import { ReactNode, useMemo } from 'react';
import { formatNumber } from 'utils/numberFormat';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { What } from 'views/Aide/Scenario/PolicyTable';
import { durationDisplay } from '../ImplementData/utils';
import { Invalid } from './ShopeePerformanceTable';
import { isStoreAliasFirst, useStoreNameConfig } from 'hooks/business/store/useStoreName';

type RT = Dashboard.PlatformData.StorePerfData;

function TiktokPerformanceTable(
  props: TableProps<RT> & {
    platform: General.PlatformUpperCase;
    onCheckDetail: (record: RT) => void;
    onCheckTrend: (record: RT) => void;
  },
) {
  const {
    platform,
    dataSource: dataSourceProp,
    onCheckDetail,
    onCheckTrend,
    ...otherProps
  } = props;

  const dataSource = useMemo(() => {
    return dataSourceProp?.map(({ data, ...others }) => {
      try {
        const jsonData = Object.entries(JSON.parse(data)).reduce<any>((res, [key, value]) => {
          res[key] = isNaN(Number(value)) ? undefined : Number(value);
          return res;
        }, {});
        return {
          ...others,
          ...jsonData,
        };
      } catch (_) {
        return others;
      }
    });
  }, [dataSourceProp]);

  const { t } = useTranslate();

  const { config } = useStoreNameConfig();

  const invalidRender = (node: ReactNode, invalid: boolean) =>
    invalid ? (
      <Invalid>
        {node}
        <span>
          <ExclamationCircleFilled />
          {t('异常')}
        </span>
      </Invalid>
    ) : (
      node
    );

  const columns: TableColumnsType<any> = [
    {
      key: 'region',
      dataIndex: 'region',
      title: t('站点'),
      width: 84,
    },
    {
      key: 'storeName',
      dataIndex: 'storeName',
      title: t('店铺名称'),
      width: 190,
      render: (_, { storeAlias, storeName, region }) =>
        isStoreAliasFirst(config, platform, region) ? storeAlias || storeName : storeName,
    },
    {
      key: 'satisfaction_percentage',
      dataIndex: 'satisfaction_percentage',
      title: (
        <div>
          {t('会话满意率')}
          <Tooltip title={t('会话满意率tips')}>
            <What />
          </Tooltip>
        </div>
      ),
      width: 144,
      render: (percentage, record) => (
        <div>
          {invalidRender(formatNumber(percentage / 100, '0.0%'), percentage < 70)}
          <Button className="ml-2" type="link" onClick={() => onCheckDetail(record)}>
            {t('todo.明细')}
          </Button>
        </div>
      ),
      sorter: true,
      showSorterTooltip: false,
    },
    {
      key: 'response_percentage',
      dataIndex: 'response_percentage',
      title: (
        <div>
          {t('回复率')}
          <Tooltip
            title={t(
              '首次响应在 12 小时内的会话在所有会话中的百分比。计算指定时间段内开始的会话。 - 常见问题解答卡片等自动回复被视为 12 小时内的响应。 - 假期模式下发起的会话不计入此计算。',
            )}
          >
            <What />
          </Tooltip>
        </div>
      ),
      width: 115,
      render: (percentage) =>
        invalidRender(formatNumber(percentage / 100, '0.0%'), percentage < 90),
      sorter: true,
      showSorterTooltip: false,
    },
    {
      key: 'support_session_count',
      dataIndex: 'support_session_count',
      title: (
        <div>
          {t('买家发起会话总数')}
          <Tooltip
            title={t(
              '客户发起的聊天支持会话总数。当客户首次点击常见问题解答卡片或在聊天中发送消息（文本、图片、视频、表情符号、产品卡片、订单卡片等）时，会话开始。',
            )}
          >
            <What />
          </Tooltip>
        </div>
      ),
      width: 185,
      render: (count) => formatNumber(count, '0,0'),
      sorter: true,
      showSorterTooltip: false,
    },
    {
      key: 'response_time_mins',
      dataIndex: 'response_time_mins',
      title: (
        <div>
          {t('平均响应时长')}
          <Tooltip title={t('首次响应会话的平均时长。')}>
            <What />
          </Tooltip>
        </div>
      ),
      width: 155,
      render: (time) => invalidRender(durationDisplay(time * 60), time > 60),
      sorter: true,
      showSorterTooltip: false,
    },
    {
      key: 'operate',
      title: t('操作'),
      width: 140,
      fixed: 'right',
      render: (_, record) => (
        <Button type="link" onClick={() => onCheckTrend(record)}>
          {t('查看趋势')}
        </Button>
      ),
    },
  ];

  return <ScrollTable dataSource={dataSource} columns={columns} {...otherProps} />;
}

export default TiktokPerformanceTable;
